import { AlertTriangle, Loader2, Pencil, Power, Send, Trash2, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  getConnectorDeliveryHealth,
  getConnectorHealth,
  getEmailConnectorConfig,
  isEmailConnector,
  type ContentPublishingConnector,
  type ContentPublishingDispatch,
  type WebhookConnectorConfig,
} from "@/lib/composerCore";
import { ConnectorTypeIcon } from "./ConnectorTypeIcon";
import { formatDate, statusVariant } from "./connectorManagerHelpers";

export type OpenIndicator = { connectorId: string; phase: "opening" | "ready" } | null;

type Props = {
  connector: ContentPublishingConnector;
  dispatches: ContentPublishingDispatch[];
  busy: boolean;
  suppressed: string[] | undefined;
  openIndicator: OpenIndicator;
  canManage: boolean;
  onTest: (connector: ContentPublishingConnector) => void;
  onEdit: (connector: ContentPublishingConnector) => void;
  onToggle: (connector: ContentPublishingConnector) => void;
  onDelete: (connector: ContentPublishingConnector) => void;
  onDismissIndicator: () => void;
};

export const ConnectorRow = ({
  connector,
  dispatches,
  busy,
  suppressed,
  openIndicator,
  canManage,
  onTest,
  onEdit,
  onToggle,
  onDelete,
  onDismissIndicator,
}: Props) => {
  const health = getConnectorHealth(connector.id, dispatches);
  const successRatePct = health.successRate === null ? null : Math.round(health.successRate * 100);
  const autoDisabled =
    connector.status === "disabled" &&
    (connector.last_error_message ?? "").toLowerCase().includes("auto-disabled");
  const isEmail = isEmailConnector(connector);
  const emailCfg = isEmail ? getEmailConnectorConfig(connector) : null;
  const webhookCfg = !isEmail ? ((connector.config ?? {}) as WebhookConnectorConfig) : null;
  const indicatorActive = openIndicator?.connectorId === connector.id;

  return (
    <li
      id={`connector-${connector.id}`}
      className="flex flex-col gap-3 scroll-mt-24 rounded-md border border-border bg-card p-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <ConnectorTypeIcon type={connector.connector_type} className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{connector.display_name}</span>
          <Badge variant={statusVariant(connector.status)} className="capitalize">
            {connector.status}
          </Badge>
          <Badge variant="outline" className="capitalize">
            {connector.connector_type}
          </Badge>
          {connector.connector_type === "kit" ? (
            <span className="text-xs text-muted-foreground">Creates drafts</span>
          ) : null}
          {health.needsAttention && connector.status !== "disabled" ? (
            <Badge variant="outline" className="gap-1 border-amber-500/50 text-amber-700 dark:text-amber-400">
              <AlertTriangle className="h-3 w-3" /> Needs attention
            </Badge>
          ) : null}
          {autoDisabled ? (
            <Badge variant="destructive" className="gap-1">
              <AlertTriangle className="h-3 w-3" /> Auto-disabled after 5 consecutive failures
            </Badge>
          ) : null}
          {indicatorActive ? (
            <span
              className={
                openIndicator!.phase === "opening"
                  ? "inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                  : "inline-flex items-center gap-1 rounded-full border border-primary/40 bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary"
              }
            >
              {openIndicator!.phase === "opening" ? (
                <span aria-hidden="true" className="inline-flex items-center gap-1">
                  <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
                  Opening editor…
                </span>
              ) : (
                <>
                  <span aria-hidden="true">Editor ready</span>
                  <button
                    type="button"
                    onClick={onDismissIndicator}
                    className="ml-0.5 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-primary/70 hover:bg-primary/20 hover:text-primary focus:outline-none focus:ring-1 focus:ring-ring"
                    aria-label={`Dismiss editor ready indicator for ${connector.display_name}`}
                  >
                    <X className="h-3 w-3" aria-hidden="true" />
                  </button>
                </>
              )}
            </span>
          ) : null}
        </div>
        {isEmail && emailCfg ? (
          <div className="space-y-0.5">
            <p className="truncate text-xs text-muted-foreground">
              {emailCfg.recipients.length} recipient{emailCfg.recipients.length === 1 ? "" : "s"}
              {emailCfg.from_name ? ` · From: ${emailCfg.from_name}` : ""}
              {emailCfg.reply_to ? ` · Reply-to: ${emailCfg.reply_to}` : ""}
            </p>
            <p className="truncate text-xs text-muted-foreground" title={emailCfg.recipients.join(", ")}>
              {emailCfg.recipients.slice(0, 3).join(", ")}
              {emailCfg.recipients.length > 3 ? `, +${emailCfg.recipients.length - 3} more` : ""}
            </p>
            {(suppressed?.length ?? 0) > 0 ? (
              <Badge
                variant="outline"
                className="gap-1 border-amber-500/50 text-amber-700 dark:text-amber-400"
                title={suppressed!.join(", ")}
              >
                <AlertTriangle className="h-3 w-3" />
                {suppressed!.length} of {emailCfg.recipients.length} suppressed and will be skipped
              </Badge>
            ) : null}
          </div>
        ) : (
          <p className="truncate text-xs text-muted-foreground" title={webhookCfg?.target_url}>
            {webhookCfg?.target_url || "No URL configured"}
          </p>
        )}
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <span>Last used: {formatDate(connector.last_used_at)}</span>
          <span>Last success: {formatDate(health.lastSuccessAt)}</span>
          <span>Last failure: {formatDate(health.lastFailureAt)}</span>
          {successRatePct !== null ? (
            <span>Success rate: {successRatePct}% (last {health.total})</span>
          ) : (
            <span>No recent dispatches</span>
          )}
        </div>
        {isEmail
          ? (() => {
              const delivery = getConnectorDeliveryHealth(connector.id, dispatches, 7);
              if (delivery.recipientsAttempted === 0) return null;
              const ratePct =
                delivery.deliveryRate === null ? null : Math.round(delivery.deliveryRate * 100);
              const failures = delivery.rejected + delivery.bounced + delivery.complained;
              const ratePoor = ratePct !== null && ratePct < 90;
              return (
                <div
                  className={`flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs ${ratePoor ? "text-amber-700 dark:text-amber-400" : "text-muted-foreground"}`}
                  title={`Last 7 days · ${delivery.dispatches} dispatch${delivery.dispatches === 1 ? "" : "es"}`}
                >
                  <span>
                    7-day delivery: {ratePct}% ({delivery.accepted}/{delivery.recipientsAttempted})
                  </span>
                  {failures > 0 ? (
                    <span>
                      · {delivery.rejected} rejected · {delivery.bounced} bounced · {delivery.complained} complained
                    </span>
                  ) : null}
                </div>
              );
            })()
          : null}
        {connector.last_error_message && (
          <p className="text-xs text-destructive">Last error: {connector.last_error_message}</p>
        )}
        {autoDisabled ? (
          <p className="text-xs text-muted-foreground">
            Run a successful test to clear the error and re-enable this connector.
          </p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant={autoDisabled || health.needsAttention ? "default" : "outline"}
          size="sm"
          onClick={() => onTest(connector)}
          disabled={!canManage || busy}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {autoDisabled ? "Run test to re-enable" : "Test"}
        </Button>
        <Button variant="outline" size="sm" onClick={() => onEdit(connector)} disabled={!canManage || busy}>
          <Pencil className="h-4 w-4" /> Edit
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onToggle(connector)}
          disabled={!canManage || busy || autoDisabled}
          title={autoDisabled ? "Run a successful test to re-enable" : undefined}
        >
          <Power className="h-4 w-4" />
          {connector.status === "active" ? "Disable" : "Enable"}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(connector)} disabled={!canManage || busy}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
};
